import React from 'react';
import { Building2, Warehouse, Zap, Wrench, Check, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { services, toolsEquipment } from '../mock';

const iconMap = {
  building: Building2,
  warehouse: Warehouse,
  zap: Zap,
  wrench: Wrench
};

const Services = () => {
  // Create URL-friendly IDs for each service
  const getServiceId = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive construction solutions tailored to your needs
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isConstructionService = service.id === 1;
            const isGeneratorService = service.id === 3;
            const isToolsService = service.id === 4;
            const serviceId = getServiceId(service.title);
            
            return (
              <Card
                key={service.id}
                id={serviceId}
                className={`group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-gray-100 hover:border-blue-200 scroll-mt-24 ${
                  isConstructionService ? 'md:col-span-2' : ''
                }`}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                    <IconComponent className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-2xl text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className={`grid gap-3 ${
                    isConstructionService ? 'md:grid-cols-3 sm:grid-cols-2' : ''
                  }`}>
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Tools & Equipment Accordion */}
                  {isToolsService && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Complete Tools & Equipment Catalog</h4>
                      <Accordion type="single" collapsible className="w-full">
                        {toolsEquipment.map((category) => (
                          <AccordionItem key={category.id} value={`item-${category.id}`}>
                            <AccordionTrigger className="text-left hover:text-blue-600">
                              <span className="font-semibold">{category.category}</span>
                            </AccordionTrigger>
                            <AccordionContent>
                              {category.subcategories ? (
                                <div className="space-y-4">
                                  {category.subcategories.map((subcat, idx) => (
                                    <div key={idx}>
                                      <h5 className="font-semibold text-gray-900 mb-2">{subcat.name}</h5>
                                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-4">
                                        {subcat.items.map((item, itemIdx) => (
                                          <li key={itemIdx} className="flex items-start text-sm text-gray-600">
                                            <Check className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {category.items.map((item, itemIdx) => (
                                    <li key={itemIdx} className="flex items-start text-sm text-gray-600">
                                      <Check className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-700 text-center">
                          <span className="font-semibold">Need something specific?</span> We can source and supply any construction tool or equipment. Contact us for availability and pricing.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Generator Promotion Link */}
                  {isGeneratorService && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <button
                        onClick={() => scrollToSection('generator-promotion')}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center"
                      >
                        <Zap className="w-5 h-5 mr-2" />
                        View Current Promotion & All Sizes
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;